import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
import { useTenant, useTenantHomePath } from "@/lib/TenantContext";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { clearPending } from "@/lib/pendingBooking";
// Relative: gateway/ has no "@/" Jest shim yet (see gateway/__testutils__/mocks.js) —
// resolved for real, same convention as every other gateway-consuming component.
import { readPendingConsult } from "../lib/pendingConsult";
import ConsultRoomFrame from "./ConsultRoomFrame";

// Stocke des clés i18n, jamais des t() au niveau module (la langue gèlerait au chargement).
const TITLES = {
  "carte-cadeau": { okKey: "checkout.giftCard.title", descKey: "checkout.giftCard.desc" },
  boutique: { okKey: "checkout.shop.title", descKey: "checkout.shop.desc" },
  facture: { okKey: "checkout.invoice.title", descKey: "checkout.invoice.desc" },
  programme: { okKey: "checkout.program.title", descKey: "checkout.program.desc" },
  reservation: { okKey: "checkout.booking.title", descKey: "checkout.booking.desc" },
  // crm-module BEGIN C2.03 (lane F2) — public session-pack purchase
  forfait: { okKey: "checkout.pack.title", descKey: "checkout.pack.desc" },
  // crm-module END
};

export default function CheckoutSuccess() {
  const t = useT("public");
  const { tenant } = useTenant();
  // BUG-039: "/" is the SaaS marketing page on the platform apex, not this tenant's site.
  const homePath = useTenantHomePath();
  const { kind } = useParams();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | failed | timeout
  const [receiptUrl, setReceiptUrl] = useState(null);
  // The practitioner's invoice number (the poll's `invoice_number`, present only for a paid
  // invoice), for /merci/facture only.
  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const attemptsRef = useRef(0);

  // Flow C's return path is a fixed literal, "/merci/consultation" (the intent body,
  // gateway-api-contract.md §2) — this is the ONE kind that does not poll the legacy
  // `/checkout/status/{sessionId}` endpoint at all: the appointment/token pair
  // `ConsultBooking.jsx` stashed right before the redirect (`lib/pendingConsult.js`) is
  // what identifies the consult, and `ConsultRoomFrame` owns its own quote/session
  // polling once mounted below. Read once, lazily (not on every render).
  //
  // AND NOT CLEARED HERE. This page is not a receipt — `ConsultRoomFrame` mounts on it,
  // so this page IS the room. A mount effect used to run `clearPendingConsult()`, which
  // meant ANY reload of this URL — a refresh, a crash, a phone locking and the tab being
  // restored — lost the appointment/token pair for good: the URL carries no query params
  // (that is the entire reason the stash exists), so there is no way back. The patient's
  // room disappears while the practitioner's clock runs on, and with the billable
  // terminal taken as the EARLIER party's last-seen the whole span pins to the instant
  // that tab died. The stash expires on its own TTL and the next booking overwrites it.
  const [pendingConsult] = useState(() => (kind === "consultation" ? readPendingConsult() : null));

  useEffect(() => {
    if (kind === "consultation") return; // ConsultRoomFrame handles its own state
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      while (!cancelled && attemptsRef.current < 8) {
        attemptsRef.current += 1;
        try {
          const { data } = await api.get(`/checkout/status/${sessionId}`);
          if (data.payment_status === "paid") {
            // Le maintien de place a fait son office : la réservation existe, le mémo
            // de reprise (résumable) n'a plus de session à reprendre. NE JAMAIS clear
            // sur failed/timeout — le maintien peut encore être payable.
            if (kind === "reservation") clearPending();
            // `receipt_url` is only present when paid AND the receipt exists (backend
            // contract) — absence-tolerant: an older/incomplete payload with no field
            // just leaves this null and the link never renders.
            if (!cancelled) setReceiptUrl(data.receipt_url || null);
            // Top-level, not `metadata.*`: the status endpoint never forwards Stripe's
            // metadata (public endpoint, narrowed disclosure — payments/router.py).
            const number = data.invoice_number;
            if (!cancelled) setInvoiceNumber(typeof number === "string" && number.trim() ? number : null);
            if (!cancelled) setStatus("paid");
            return;
          }
          if (data.status === "expired") {
            if (!cancelled) setStatus("failed");
            return;
          }
        } catch (e) {
          // continue
        }
        await new Promise((r) => setTimeout(r, 2500));
      }
      if (!cancelled) setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, kind]);

  const info = TITLES[kind] || TITLES.boutique;
  // Who the confirmation says will follow up. Same fallback chain as the
  // transactional emails (backend/emails/shared.py: doc.name || doc.owner_name), then a
  // generic subject: `tenant` is null while TenantContext is still loading and on a
  // 404'd slug, and "undefined vous contactera" is worse than "Notre équipe".
  const practitionerName =
    tenant?.name || tenant?.owner_name || t("checkout.teamFallback");

  if (kind === "consultation") {
    return (
      <ThemedPageLayout>
        <section className="pt-32 pb-20 max-w-2xl mx-auto px-6" data-testid="checkout-success">
          {pendingConsult ? (
            <ConsultRoomFrame appointmentId={pendingConsult.appointmentId}
                              token={pendingConsult.token} homePath={homePath} />
          ) : (
            <div className="text-center" data-testid="checkout-consultation-missing-context">
              <XCircle className="mx-auto text-red-500" size={42} />
              <h1 className="font-display text-4xl mt-6">{t("checkout.telehealth.title")}</h1>
              <p className="text-eh-muted mt-3">{t("checkout.consultationMissingContext")}</p>
              <Link to={homePath} data-testid="checkout-back-home" className="btn-ghost text-sm mt-8 inline-flex">{t("checkout.backHome")}</Link>
            </div>
          )}
        </section>
      </ThemedPageLayout>
    );
  }

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-20 max-w-2xl mx-auto px-6 text-center" data-testid="checkout-success">
        {status === "polling" && (
          <>
            <Loader2 className="animate-spin mx-auto text-eh-sage-deep" size={32} />
            <h1 className="font-display text-4xl mt-6">{t("checkout.polling.title")}</h1>
            <p className="text-eh-muted mt-3">{t("checkout.polling.desc")}</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-eh-sage-deep" size={42} />
            <h1 className="font-display text-4xl mt-6">{t(info.okKey)}</h1>
            {kind === "facture" && invoiceNumber && (
              <p className="font-mono text-sm text-eh-muted mt-2" data-testid="checkout-invoice-number">
                {t("checkout.invoice.number", { number: invoiceNumber })}
              </p>
            )}
            <p className="text-eh-muted mt-3" data-testid="checkout-success-desc">
              {t(info.descKey, { practitionerName })}
            </p>
            {receiptUrl && (
              <a
                href={receiptUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="checkout-receipt-link"
                className="btn-ghost text-sm mt-4 inline-flex"
              >
                {t("checkout.viewReceipt")}
              </a>
            )}
            <Link to={homePath} data-testid="checkout-back-home" className="btn-primary text-sm mt-8 inline-flex">{t("checkout.backHome")}</Link>
          </>
        )}
        {(status === "failed" || status === "timeout") && (
          <>
            <XCircle className="mx-auto text-red-500" size={42} />
            <h1 className="font-display text-4xl mt-6">{status === "failed" ? t("checkout.failed.title") : t("checkout.timeout.title")}</h1>
            <p className="text-eh-muted mt-3">
              {status === "failed"
                ? t("checkout.failed.desc")
                : t("checkout.timeout.desc")}
            </p>
            <Link to={homePath} data-testid="checkout-back-home" className="btn-ghost text-sm mt-8 inline-flex">{t("checkout.backHome")}</Link>
          </>
        )}
      </section>
    </ThemedPageLayout>
  );
}
